import React from 'react';
import { Typography, Button } from '@mui/material';
import { Link } from 'react-router-dom';

function FeatureShowcase({ title, description, stat, imagePosition = 'left', imageFile, visualPlaceholder, linkLabel, linkPath, onImageClick }) {
  const isImageLeft = imagePosition === 'left';

  // Visual placeholder content based on type
  const getPlaceholderContent = (type) => {
    const placeholders = {
      AI_AGENT_INTERFACE: {
        title: "AI AGENT INTERFACE",
        description: "Dashboard showing AI Agent sidebar with multiple suggestions in queue, preview cards, and 'Human-in-the-Loop' oversight controls"
      },
      REFERRAL_MATCHING_INTERFACE: {
        title: "REFERRAL MATCHING",
        description: "Search interface with filters (specialty, insurance, location) and provider cards showing 'Perfect Match' indicators"
      },
      BILLING_SNAPSHOT_INTERFACE: {
        title: "BILLING SNAPSHOT",
        description: "Split view showing session details form and generated CMS-1500 superbill with 'Snapshot Created' timestamp and audit trail"
      },
      CARE_TEAM_INTERFACE: {
        title: "CARE TEAM COLLABORATION",
        description: "Team chat interface showing therapist, psychiatrist, and parent (guest) coordination with file sharing and video call options"
      },
      AI_ADMIN_INTERFACE: {
        title: "AI ADMIN INTERFACE",
        description: "24/7 public booking with automated verification and AI-powered intake management"
      },
      CLINICAL_RECORDS_INTERFACE: {
        title: "CLINICAL RECORDS",
        description: "Complete EHR with 17 pre-built templates and secure provider collaboration"
      }
    };
    return placeholders[type] || { title: "FEATURE VISUAL", description: "Feature visual placeholder" };
  };

  const placeholderContent = getPlaceholderContent(visualPlaceholder);

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12 relative">
      <div className={`flex flex-col ${isImageLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12 lg:gap-16`}>
        {/* Image/Visual */}
        <div className="flex-1">
          {imageFile ? (
            <img
              src={imageFile}
              alt={title}
              className="w-full h-auto rounded-xl shadow-md cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => onImageClick && onImageClick(imageFile)}
            />
          ) : (
            <div className="bg-gray-100 rounded-xl p-8 min-h-96 flex items-center justify-center border-2 border-dashed border-gray-300">
              <div className="text-center text-gray-500 max-w-sm">
                <Typography variant="h6" className="mb-4 font-semibold">
                  {placeholderContent.title}
                </Typography>
                <Typography variant="body2" className="leading-relaxed">
                  {placeholderContent.description}
                </Typography>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className={`flex-1 ${isImageLeft ? '' : 'lg:pl-4'}`}>
          <Typography variant="h3" className="text-3xl font-bold text-gray-900 mb-6">
            {title}
          </Typography>
          <Typography variant="body1" className="text-lg text-gray-700 mb-8 leading-relaxed">
            {description}
          </Typography>
          {stat && (
            <div className="bg-blue-50 rounded-lg inline-block">
              <div className="px-8 py-4">
                <h4 className="text-2xl font-bold text-blue-600 m-0 p-0 leading-none">
                  {stat}
                </h4>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {linkLabel && linkPath && (
        <Button
          component={Link}
          to={linkPath}
          variant="text"
          className={`text-blue-600 hover:text-blue-800 font-semibold absolute bottom-8 ${isImageLeft ? 'right-8' : 'left-8'} hidden lg:block`}
        >
          {linkLabel} →
        </Button>
      )}
      
      {/* Mobile button - positioned in content flow */}
      {linkLabel && linkPath && (
        <div className="lg:hidden mt-4 flex justify-center">
          <Button
            component={Link}
            to={linkPath}
            variant="text"
            className="text-blue-600 hover:text-blue-800 font-semibold"
          >
            {linkLabel} →
          </Button>
        </div>
      )}
    </div>
  );
}

export default FeatureShowcase;